import { Navigate, useLocation } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireApproval?: boolean;
}

/**
 * A wrapper component that protects routes requiring authentication.
 * Redirects unauthenticated users to the specified path (default: /).
 * If requireApproval is true, also checks if user is approved.
 *
 * Usage:
 * ```tsx
 * // As a layout wrapper for nested routes:
 * <Route path="/app" element={<AppLayout />}>
 *   <Route index element={<Dashboard />} />
 *   <Route path="settings" element={<Settings />} />
 * </Route>
 *
 * // Or wrap individual routes:
 * <Route path="/protected" element={<ProtectedRoute><Page /></ProtectedRoute>} />
 * ```
 */
export function ProtectedRoute({
  children,
  redirectTo = '/',
  requireApproval = true,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // Redirect to login/home if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check if approval is required and user is not approved
  if (requireApproval && user && !user.approved) {
    // Don't redirect if already on the pending approval page
    if (location.pathname !== '/app/pending-approval') {
      return <Navigate to="/app/pending-approval" replace />;
    }
  }

  return <>{children}</>;
}
